<template>
  <div class="project-details-card">
    <div class="card mb-4">
      <div class="card-header"><h5 class="mb-0">Details</h5></div>
      <div class="card-body">

        <div class="mb-3">
          <label class="control-label">Calculation Name <span class="text-danger">*</span></label>
          <input
            v-model="projectCalculation.name"
            type="text"
            class="form-control"
            name="name"
            v-validate="'required'"
            data-vv-as="name"
          />
          <help-block field="name" />
        </div>

        <div class="mb-3">
          <label class="control-label">Client</label>
          <multiselect
            :show-labels="false"
            v-model="selectedClient"
            :options="clients"
            label="display_name"
            track-by="id"
            placeholder="Select a client…"
            :allow-empty="true"
            @input="onClientInput"
          />
        </div>

        <div class="mb-3" v-if="projectCalculation.opportunity">
          <label class="control-label">Linked Opportunity</label>
          <p class="form-control-plaintext">
            <router-link :to="{ name: 'opportunities.overview', params: { opportunity_id: projectCalculation.opportunity.id } }">
              {{ projectCalculation.opportunity.name }}
            </router-link>
          </p>
        </div>

      </div>
    </div>

    <div class="card mb-4">
      <div class="card-header"><h5 class="mb-0">Project Details</h5></div>
      <div class="card-body">

        <div class="row mb-5">
          <div class="col-sm-12 mb-12">
            <label class="control-label">Base Effort Estimation <span class="text-danger">*</span></label>
            <div class="input-group">
              <input v-model.number="projectCalculation.base_effort_estimation" type="number" step="0.5" min="0" class="form-control" name="base_effort_estimation" v-validate="'required|min_value:0'" data-vv-as="base effort estimation" />
              <span class="input-group-text">hrs</span>
              <button
                type="button"
                class="btn btn-outline-secondary"
                title="Calculate from budget"
                :disabled="errors.items.some(e => e.field !== 'base_effort_estimation')"
                @click="$emit('open-budget-calculator')"
              >
                <i class="fal fa-calculator"></i>
              </button>
            </div>
            <help-block field="base_effort_estimation" />
          </div>
        </div>

        <hr/>

        <div class="row mt-5">
          <div class="col-sm-6 mb-3">
            <label class="control-label">Dev Days <span class="text-danger">*</span> <i class="fal fa-info-circle calc-info" title="Days per sprint developers are developing on user stories"></i></label>
            <input
              v-model.number="projectCalculation.dev_days"
              type="number"
              min="0.5"
              step="0.5"
              class="form-control"
              name="dev_days"
              v-validate="'required|min_value:0.5'"
              data-vv-as="dev days"
            />
            <help-block field="dev_days" />
          </div>

          <div class="col-sm-6 mb-3">
            <label class="control-label">No. of Developers <span class="text-danger">*</span></label>
            <multiselect
              :show-labels="false"
              v-model="projectCalculation.num_devs"
              :options="[1, 2, 3, 4, 5]"
              :allow-empty="false"
              name="num_devs"
              v-validate="'required'"
              data-vv-as="number of developers"
            />
            <help-block field="num_devs" />
          </div>

          <div class="col-sm-6 mb-3">
            <label class="control-label">Sprint Rituals Estimate <span class="text-danger">*</span></label>
            <div class="input-group">
              <input v-model.number="projectCalculation.sprint_rituals_estimate" type="number" step="0.5" min="0" class="form-control" name="sprint_rituals_estimate" v-validate="'required|min_value:0'" data-vv-as="sprint rituals estimate" />
              <span class="input-group-text">hrs / sprint</span>
            </div>
            <help-block field="sprint_rituals_estimate" />
          </div>

          <div class="col-sm-6 mb-3">
            <label class="control-label">Scoping Sprint Testing <span class="text-danger">*</span></label>
            <div class="input-group">
              <input v-model.number="projectCalculation.scoping_sprint_testing" type="number" step="0.5" min="0" class="form-control" name="scoping_sprint_testing" v-validate="'required|min_value:0'" data-vv-as="scoping sprint testing" />
              <span class="input-group-text">days / sprint</span>
            </div>
            <help-block field="scoping_sprint_testing" />
          </div>

          <div class="col-sm-6 mb-3">
            <label class="control-label">Project Management <span class="text-danger">*</span></label>
            <div class="input-group">
              <input v-model.number="projectCalculation.project_management" type="number" step="0.5" min="0" class="form-control" name="project_management" v-validate="'required|min_value:0'" data-vv-as="project management" />
              <span class="input-group-text">hrs / sprint</span>
            </div>
            <help-block field="project_management" />
          </div>

          <div class="col-sm-6 mb-3">
            <label class="control-label">Evaluation <span class="text-danger">*</span></label>
            <div class="input-group">
              <input v-model.number="projectCalculation.evaluation" type="number" step="0.5" min="0" class="form-control" name="evaluation" v-validate="'required|min_value:0'" data-vv-as="evaluation" />
              <span class="input-group-text">days</span>
            </div>
            <help-block field="evaluation" />
          </div>

          <div class="col-sm-6 mb-3">
            <label class="control-label">Contingency Days <span class="text-danger">*</span> <i class="fal fa-info-circle calc-info" title="To cater for leave/sick days and unforseen complications"></i></label>
            <multiselect
              :show-labels="false"
              v-model="projectCalculation.contingency_days"
              :options="contingencyOptions"
              :custom-label="day => day === 1 ? '1 day' : `${day} days`"
              :allow-empty="false"
              name="contingency_days"
              v-validate="'required'"
              data-vv-as="contingency days"
            />
            <help-block field="contingency_days" />
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { mapState, mapActions } from 'vuex'

export default {
  $_veeValidate: { validator: 'new' },

  components: { Multiselect },

  props: {
    projectCalculation: { type: Object, required: true },
  },

  data() {
    return {
      contingencyOptions: Array.from({ length: 11 }, (_, i) => i),
    }
  },

  computed: {
    ...mapState('clientsList', ['clients']),

    selectedClient: {
      get() {
        return this.clients.find(c => c.id === this.projectCalculation.client_id) || null
      },
      set(client) {
        console.log(client)
        this.projectCalculation.client_id = client
      },
    },
  },

  created() {
    this.getClients()
  },

  methods: {
    ...mapActions('clientsList', ['getClients']),

    onClientInput(client) {
      this.projectCalculation.client_id = client ? client.id : null
    },
  },
}
</script>

<style>
/* Remove the checkmark icon from valid fields in this card only, keep the green border */
.project-details-card .is-valid {
  background-image: none;
}
</style>

<style scoped>
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}
.calc-info {
  cursor: help;
  color: #6c757d;
  font-size: 0.75rem;
  margin-left: 3px;
  vertical-align: middle;
  opacity: 0.6;
}
.calc-info:hover {
  opacity: 1;
}
</style>
